import { Flooding } from 'features/flooding/flooding.types';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import { getMarkerMoveDistanceMiles } from 'features/geoEvents/utils/markerMoveDistance';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { hasRegionsChanged } from './useGeoEventGeolocationFormFieldChanges.utils';

type UseGeoEventGeolocationFormFieldChangesProps = {
  geoEvent: Wildfire | Flooding;
};

type FormValues = {
  name: string;
  address?: string;
  lat: number;
  lng: number;
  regions: GeoEventRegion[];
};

type ChangeKey = keyof FormValues | 'distance';

type ChangeMap = Partial<Record<ChangeKey, string>>;

export const useGeoEventGeolocationFormFieldChanges = (
  props: UseGeoEventGeolocationFormFieldChangesProps,
): ChangeMap => {
  const { geoEvent } = props;
  const { t } = useTranslation();

  const [name, address, lat, lng, regions] = useWatch<FormValues>({
    name: ['name', 'address', 'lat', 'lng', 'regions'],
  }) as [string, string, number, number, GeoEventRegion[]];

  const nameChanged = (geoEvent?.name ?? '').trim() !== (name ?? '').trim();
  const latChanged = geoEvent?.lat !== lat;
  const lngChanged = geoEvent?.lng !== lng;

  const isComplexParent =
    geoEvent.geoEventType === 'wildfire' &&
    Boolean(geoEvent.data?.isComplexParent);

  // Complex parents only have their `regions` auto-derived from child
  // wildfires' regions once they have at least one child; until then,
  // `regions` is still directly editable and its changes are meaningful.
  const isComplexParentWithChildren =
    isComplexParent && (geoEvent as Wildfire).childGeoEvents.length > 0;

  // Only wildfires have editable address fields
  // A bot-managed incident often has no address at all, and the form seeds the
  // field with ''. Normalize so nullish and empty don't read as a change.
  const addressChanged =
    geoEvent.geoEventType === 'wildfire' &&
    !isComplexParent &&
    (geoEvent?.address ?? '').trim() !== (address ?? '').trim();
  const regionsChanged =
    !isComplexParentWithChildren &&
    hasRegionsChanged(geoEvent?.regions ?? [], regions);

  const changes: ChangeMap = {};
  if (nameChanged) {
    changes.name = t('geoEventChangelog.name', {
      prevValue: geoEvent.name,
      newValue: name,
    });
  }
  if (addressChanged) {
    changes.address = t('geoEventChangelog.address', {
      prevValue: geoEvent.address,
      newValue: address,
    });
  }
  if (latChanged) {
    changes.lat = t('geoEventChangelog.latitude', {
      prevValue: geoEvent.lat.toFixed(6),
      newValue: lat.toFixed(6),
    });
  }
  if (lngChanged) {
    changes.lng = t('geoEventChangelog.longitude', {
      prevValue: geoEvent.lng.toFixed(6),
      newValue: lng.toFixed(6),
    });
  }
  if (latChanged || lngChanged) {
    changes.distance = t('geoEventChangelog.distanceMoved', {
      miles: getMarkerMoveDistanceMiles(
        { lat: geoEvent.lat, lng: geoEvent.lng },
        { lat, lng },
      ).toFixed(2),
    });
  }
  if (regionsChanged) {
    changes.regions = t('geoEventChangelog.regions');
  }

  return changes;
};
